import { defineStore } from 'pinia';
import { assistantService, AssistantError } from '@/services/assistantService';

const MAX_MESSAGES = 30;

/**
 * Assistant conversation state.
 *
 * Mirrors the backend's stateless model: the full transcript is sent on every
 * `sendMessage` call. Pending write-actions returned by the backend are tracked
 * inline on the assistant message they belong to, so the thread can render
 * confirm/dismiss UI per action.
 */
export const useAssistantStore = defineStore('assistant', {
  state: () => ({
    /**
     * Messages in chronological order.
     * Shape:
     *   {
     *     id, role: 'user'|'assistant', content, created_at,
     *     // assistant-only:
     *     pending_actions?: [{ id, tool, summary, preview, expires_at, status?: 'pending'|'confirmed'|'dismissed'|'expired'|'failed', result?, error? }],
     *     tool_results?: [{ tool, tool_call_id, data }],
     *     isError?: boolean,
     *   }
     */
    messages: [],
    sending: false,
    /** Last AssistantError, useful for showing inline upgrade prompts (rate limit, service down, ...) */
    lastError: null,
  }),

  getters: {
    hasConversation: (state) => state.messages.length > 0,
    lastMessage: (state) => state.messages[state.messages.length - 1] || null,
  },

  actions: {
    async sendMessage(content) {
      const trimmed = content?.trim();
      if (!trimmed || this.sending) return;

      const userMsg = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: trimmed,
        created_at: new Date().toISOString(),
      };

      this.messages.push(userMsg);
      this.sending = true;
      this.lastError = null;

      try {
        // Build payload: only role+content, capped to most recent N to avoid
        // hitting the backend's 30-message limit even after long sessions.
        const payload = this.messages
          .filter((m) => m.role === 'user' || m.role === 'assistant')
          .filter((m) => !m.isError)
          .slice(-MAX_MESSAGES)
          .map((m) => ({ role: m.role, content: m.content }));

        const reply = await assistantService.sendMessage(payload);

        // Tag each pending action with status='pending' for the UI state machine.
        if (reply.pending_actions?.length) {
          reply.pending_actions = reply.pending_actions.map((a) => ({
            ...a,
            status: 'pending',
          }));
        }

        this.messages.push(reply);
      } catch (error) {
        this.lastError = error instanceof AssistantError ? error : null;
        this.messages.push(buildErrorMessage(error));
        throw error;
      } finally {
        this.sending = false;
      }
    },

    async confirmAction(actionId) {
      const { message, action } = this.findAction(actionId);
      if (!message || !action || action.status !== 'pending') return;

      action.status = 'confirming';
      try {
        const result = await assistantService.confirmAction(actionId);
        action.status = 'confirmed';
        action.result = result?.result || null;
      } catch (error) {
        if (error instanceof AssistantError && error.code === 'action_expired') {
          action.status = 'expired';
        } else {
          action.status = 'failed';
          action.error = error?.message || 'Bevestigen mislukt';
        }
        throw error;
      }
    },

    dismissAction(actionId) {
      const { action } = this.findAction(actionId);
      if (action && action.status === 'pending') {
        action.status = 'dismissed';
      }
    },

    findAction(actionId) {
      for (const message of this.messages) {
        if (!message.pending_actions) continue;
        const action = message.pending_actions.find((a) => a.id === actionId);
        if (action) return { message, action };
      }
      return { message: null, action: null };
    },

    reset() {
      this.messages = [];
      this.sending = false;
      this.lastError = null;
    },
  },
});

function buildErrorMessage(error) {
  const code = error?.code;
  let content = '⚠️ Sorry, er ging iets mis. Probeer het opnieuw.';

  if (code === 'rate_limited') {
    content = '⏱️ Je hebt veel vragen achter elkaar gesteld. Probeer het over een minuut opnieuw.';
  } else if (code === 'service_unavailable') {
    content = '🔧 De AI-assistent is nog niet geconfigureerd. Vraag een beheerder om dit aan te zetten.';
  } else if (code === 'upstream_error') {
    content = '🌐 De AI-service is even niet bereikbaar. Probeer het zo opnieuw.';
  } else if (code === 'validation_error') {
    content = '⚠️ ' + (error.message || 'Je bericht kon niet worden verwerkt.');
  }

  return {
    id: `error-${Date.now()}`,
    role: 'assistant',
    content,
    created_at: new Date().toISOString(),
    isError: true,
  };
}
